import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Music, TrendingUp, FlaskConical, Shirt, Scissors, MessageSquareHeart, Landmark, Cpu, Heart, Film, BookOpen, Search, Filter, Settings, Loader , ChevronLeft, ChevronRight } from 'lucide-react';
import NavbarLanding from '../components/NavbarLanding';
import Navigation from '../components/Navigation';
import { db } from '../lib/firebase';
import { ref, get, set } from 'firebase/database';
import { API_BASE_URL } from '../config/api';

const ALL_TOPICS = [
  { id: 'music', icon: Music, label: 'Music' },
  { id: 'economy', icon: TrendingUp, label: 'Economy' },
  { id: 'science', icon: FlaskConical, label: 'Science' },
  { id: 'clothes', icon: Shirt, label: 'Clothes' },
  { id: 'beauty', icon: Scissors, label: 'Beauty' },
  { id: 'gossip', icon: MessageSquareHeart, label: 'Gossip' },
  { id: 'politics', icon: Landmark, label: 'Politics' },
  { id: 'technology', icon: Cpu, label: 'Tech' },
  { id: 'health', icon: Heart, label: 'Health' },
  { id: 'entertainment', icon: Film, label: 'Entertainment' },
  { id: 'world', icon: BookOpen, label: 'General' },
];

export default function NewsAI() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isFetchingRecent, setIsFetchingRecent] = useState(false);
  const [articles, setArticles] = useState<any[]>([]);
  
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [showSettings, setShowSettings] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [maxKnownPage, setMaxKnownPage] = useState(7);
  const [feedDate, setFeedDate] = useState<string>('');
  const [userId, setUserId] = useState<string | null>(null);

  const deduplicateArticles = (items: any[]) => {
    const seenTitles = new Set();
    const seenUrls = new Set();
    const unique: any[] = [];
    for (const article of items) {
      if (!article || !article.title) continue;
      const normalizedTitle = article.title.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
      if (!seenTitles.has(normalizedTitle) && (!article.url || !seenUrls.has(article.url))) {
        seenTitles.add(normalizedTitle);
        if (article.url) seenUrls.add(article.url);
        unique.push(article);
      }
    }
    return unique.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
  };

  const getDateForPage = (page: number) => {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() - (page - 1));
    return targetDate.toISOString().split('T')[0];
  };

  const getCachedArticlesForDate = async (topics: string[], dateStr: string): Promise<any[]> => {
    const all: any[] = [];
    await Promise.all(
      topics.map(async (topic) => {
        try {
          const cacheRef = ref(db, `newsArticles/live_feed_cache/${dateStr}_${topic}`);
          const snapshot = await get(cacheRef);
          if (snapshot.exists()) {
            const val = snapshot.val();
            if (Array.isArray(val)) {
              all.push(...val);
            }
          }
        } catch (e) {
          console.error('Error fetching cached news from RTDB:', e);
        }
      })
    );
    return deduplicateArticles(all);
  };

  const findRecentCachedArticles = async (topics: string[]): Promise<{ articles: any[]; date: string } | null> => {
    for (let offset = 0; offset <= 7; offset++) {
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() - offset);
      const dateStr = targetDate.toISOString().split('T')[0];
      const cached = await getCachedArticlesForDate(topics, dateStr);
      if (cached.length > 0) {
        return { articles: cached, date: dateStr };
      }
    }
    return null;
  };

  const saveArticlesToRTDB = async (dateStr: string, items: any[]) => {
    try {
      const byTopic: Record<string, any[]> = {};
      for (const item of items) {
        if (item && item.topic) {
          if (!byTopic[item.topic]) byTopic[item.topic] = [];
          byTopic[item.topic].push(item);
        }
      }
      await Promise.all(
        Object.entries(byTopic).map(([topic, topicArticles]) =>
          set(ref(db, `newsArticles/live_feed_cache/${dateStr}_${topic}`), topicArticles)
        )
      );
    } catch (e) {
      console.error('Error caching articles to RTDB:', e);
    }
  };

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userObj = JSON.parse(userStr);
      setUserId(userObj.uid);
      loadPreferences(userObj.uid);
    } else {
      const guestTopics = localStorage.getItem('guestTopics');
      if (guestTopics) {
        const parsed = JSON.parse(guestTopics);
        setSelectedTopics(parsed);
        fetchFeed(parsed, 1);
      } else {
        setLoading(false);
      }
    }
  }, []);

  const loadPreferences = async (uid: string) => {
    try {
      const prefRef = ref(db, `users/${uid}/newsTopics`);
      const snapshot = await get(prefRef);
      if (snapshot.exists()) {
        const topics = snapshot.val();
        setSelectedTopics(topics);
        fetchFeed(topics, 1);
      } else {
        setLoading(false);
      }
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const fetchFeed = async (topics: string[], page: number = 1) => {
    if (!topics || topics.length === 0) return;

    const targetDateStr = getDateForPage(page);

    // If page > 1 (browsing specific older day), check RTDB first
    if (page > 1) {
      const cachedForPage = await getCachedArticlesForDate(topics, targetDateStr);
      if (cachedForPage.length > 0) {
        setArticles(cachedForPage);
        setFeedDate(targetDateStr);
        setLoading(false);
        setIsFetchingRecent(false);
        return;
      }
    }

    // If page === 1, check if we have cached news for today or recent days to display immediately
    if (page === 1) {
      setIsFetchingRecent(true);
      const recentCached = await findRecentCachedArticles(topics);
      if (recentCached && recentCached.articles.length > 0) {
        setArticles(recentCached.articles);
        setFeedDate(recentCached.date);
        setLoading(false);
      }
    } else {
      setLoading(true);
    }

    try {
      const topicsQuery = topics.join(',');
      const res = await fetch(`${API_BASE_URL}/api/news/feed?type=ai&topics=${encodeURIComponent(topicsQuery)}&page=${page}`);
      const data = await res.json();
      if (data.success && data.articles) {
        setArticles(data.articles);
        if (data.date) {
          setFeedDate(data.date);
          saveArticlesToRTDB(data.date, data.articles);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setIsFetchingRecent(false);
    }
  };

  const handleTopicToggle = async (tId: string) => {
    let newTopics = [];
    if (selectedTopics.includes(tId)) {
      newTopics = selectedTopics.filter(id => id !== tId);
    } else {
      newTopics = [...selectedTopics, tId];
    }
    
    setSelectedTopics(newTopics);
    
    // Save preferences silently
    try {
      if (userId) {
        await set(ref(db, `users/${userId}/newsTopics`), newTopics);
      }
      localStorage.setItem('guestTopics', JSON.stringify(newTopics));
    } catch (e) {
      console.error(e);
    }
    
    // Refresh feed instantly with new array
    setCurrentPage(1);
    fetchFeed(newTopics, 1);
  };

  const handlePageChange = (page: number) => {
    if (page < 1) return;
    setCurrentPage(page);
    fetchFeed(selectedTopics, page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleArticleClick = (article: any) => {
    navigate(`/news/article/${article.id}`, { 
      state: { 
        rawArticle: article, 
        source: 'ai'
      } 
    });
  };

  const getTopicLabel = (topicId: string) => {
    return ALL_TOPICS.find(t => t.id === topicId)?.label || topicId;
  };

  return (
    <>
      <NavbarLanding isLanding={false} />
      <Navigation />
      <div className="min-h-screen bg-gray-50">
        <main className="flex-1 pb-16 md:pb-0 md:ml-64 p-6">
          <div className="max-w-5xl mx-auto space-y-6">
            
            <header className="flex flex-col gap-2 bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <Sparkles className="w-6 h-6 text-indigo-600" />
                    Daily News
                  </h1>
                  <p className="text-gray-600 text-sm mt-1">Live news on-demand to your exact English level so you can eead and practice!</p>
                  {feedDate && selectedTopics.length > 0 && !loading && (
                    <p className="text-indigo-600 font-bold text-sm mt-2">
                      📅 {(() => {
                        const [y, m, d] = feedDate.split('-');
                        return `${d}/${m}/${y}`;
                      })()}
                    </p>
                  )}
                  

                </div>
                {selectedTopics.length > 0 && (
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                  >
                    <Settings className="w-5 h-5" />
                  </button>
                )}
              </div>

              {(showSettings || selectedTopics.length === 0) && (
                <div className="w-full mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                    {selectedTopics.length === 0 ? "Step 1: Select topics to build your feed" : "Update your interests"}
                  </p>
                  <div className="flex flex-wrap gap-2 pb-2">
                    {ALL_TOPICS.map(t => {
                      const isSelected = selectedTopics.includes(t.id);
                      return (
                        <button
                          key={t.id}
                          onClick={() => handleTopicToggle(t.id)}
                          className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all border flex items-center gap-1.5 ${
                            isSelected
                              ? 'bg-indigo-600 text-white border-indigo-600 shadow-md'
                              : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <t.icon className="w-4 h-4" />
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                  {showSettings && selectedTopics.length > 0 && (
                    <div className="flex justify-end mt-2">
                      <button onClick={() => setShowSettings(false)} className="text-sm px-4 py-2 rounded-lg font-bold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors">
                        Done
                      </button>
                    </div>
                  )}
                </div>
              )}
            </header>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader className="w-10 h-10 animate-spin text-indigo-600 mb-4" />
                <p className="text-gray-600">Fetching live headlines...</p>
              </div>
            ) : selectedTopics.length === 0 ? (
              <div className="bg-white p-16 rounded-2xl border border-gray-200 text-center shadow-sm">
                <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Settings className="w-8 h-8 text-indigo-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Welcome to Daily News</h3>
                <p className="text-gray-500 max-w-md mx-auto text-lg">
                  Please select at least one topic from the list above to generate your personalized news feed.
                </p>
              </div>
            ) : articles.length === 0 ? (
              <div className="bg-white p-12 rounded-2xl border border-gray-200 text-center shadow-sm">
                <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">No articles found</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-6">
                  We couldn't fetch live headlines right now. Check your connection or update your topics.
                </p>
              </div>
            ) : (
              <>
                {isFetchingRecent && articles.length > 0 && (
                  <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 px-5 py-3.5 rounded-xl text-indigo-700">
                    <Loader className="w-5 h-5 animate-spin text-indigo-600 flex-shrink-0" />
                    <span className="text-sm font-medium">
                      Fetching today's latest headlines... Showing saved news from recent days in the meantime.
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {articles.map((article, idx) => (
                  <div 
                    key={`${article.id}_${idx}`} 
                    onClick={() => handleArticleClick(article)}
                    className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md hover:border-indigo-300 transition-all flex flex-col h-full group"
                  >
                    <img 
                      src={article.image || 'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?auto=format&fit=crop&q=80&w=800'} 
                      alt={article.title} 
                      className="w-full h-40 object-cover border-b border-gray-100"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?auto=format&fit=crop&q=80&w=800';
                      }}
                    />
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex justify-end mb-4">
                        <span className="text-xs text-gray-500 font-medium bg-gray-50 px-3 py-1 rounded-full border border-gray-100">
                          {(() => {
                            const d = new Date(article.publishedAt);
                            const dd = d.getDate().toString().padStart(2, '0');
                            const mm = (d.getMonth() + 1).toString().padStart(2, '0');
                            const yyyy = d.getFullYear();
                            return `${dd}/${mm}/${yyyy}`;
                          })()}
                        </span>
                      </div>
                      
                      <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors line-clamp-3">
                        {article.title}
                      </h2>
                      
                      <p className="text-gray-600 text-sm line-clamp-3 mb-4 flex-1">
                        {article.description}
                      </p>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
                        <div className="flex items-center gap-1.5 text-indigo-600 font-medium text-sm">
                          <Search className="w-4 h-4" />
                          {getTopicLabel(article.topic)}
                        </div>
                        <span className="text-indigo-600 font-bold text-sm bg-indigo-50 px-3 py-1.5 rounded-lg group-hover:bg-indigo-100 transition-colors">
                          Process AI &rarr;
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
            
            {/* Bottom Pagination */}
            {selectedTopics.length > 0 && !loading && (
                <div className="mt-8 flex justify-center pb-8">
                  <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-xl shadow-sm border border-gray-200">
                    <button 
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === maxKnownPage}
                      className="flex items-center gap-1 px-3 py-2 text-sm font-bold text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Older
                    </button>
                    
                    <div className="flex items-center gap-1 px-4 py-2 bg-indigo-50 rounded-lg">
                      <span className="text-sm font-bold text-indigo-700">
                        {feedDate ? (() => {
                          const [y, m, d] = feedDate.split('-');
                          return `${d}/${m}/${y}`;
                        })() : `Page ${currentPage}`}
                      </span>
                    </div>
                    
                    <button 
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="flex items-center gap-1 px-3 py-2 text-sm font-bold text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Newer
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
            )}
            
          </div>
        </main>
      </div>
    </>
  );
}
